import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  ArrowLeft, Plus, Edit, Trash2, Book, GraduationCap, Brain, Users,
  Eye, LayoutDashboard, Settings, ChevronDown, Save, X, Layers, Image, Mic, Volume2, Compass, FileText, Sliders, ArrowUp, ArrowDown, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import AudioRecorder from '../components/AudioRecorder';
import EmojiPicker from '../components/EmojiPicker';
import { getCategoryIcon, getSortedCategories } from '../utils/categoryConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility to force fresh data fetch (bypass browser cache)
const freshFetch = (url, options = {}) => {
  const timestamp = Date.now();
  const separator = url.includes('?') ? '&' : '?';
  const freshUrl = `${url}${separator}_t=${timestamp}`;
  return axios.get(freshUrl, {
    ...options,
    headers: {
      ...options.headers,
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  });
};

const Admin = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [levels, setLevels] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState(null); // For filtering content by level

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Accès non autorisé');
      navigate('/dashboard');
      return;
    }
    fetchData();

    // Listen for navigation events from ModuleManager
    const handleAdminNavigate = (e) => {
      const { tab, level } = e.detail;
      setActiveTab(tab);
      setLevelFilter(level);
    };
    window.addEventListener('adminNavigate', handleAdminNavigate);
    return () => window.removeEventListener('adminNavigate', handleAdminNavigate);
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, modulesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/modules`)
      ]);
      setStats(statsRes.data);
      setLevels(modulesRes.data.levels || []);
      setModules(modulesRes.data.modules || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: <LayoutDashboard className="w-5 h-5" /> },
    { id: 'settings', label: 'Paramètres', icon: <Sliders className="w-5 h-5" /> },
    { id: 'content-order', label: 'Réorganisation', icon: <Layers className="w-5 h-5" /> },
    { id: 'sounds', label: 'Sons Darija', icon: <Volume2 className="w-5 h-5" /> },
    { id: 'vocabulary', label: 'Vocabulaire', icon: <GraduationCap className="w-5 h-5" /> },
    { id: 'grammar', label: 'Grammaire', icon: <Book className="w-5 h-5" /> },
    { id: 'mini-quiz', label: 'Mini-Quiz', icon: <FileText className="w-5 h-5" /> },
    { id: 'situations', label: 'Situations', icon: <Users className="w-5 h-5" /> },
    { id: 'quiz', label: 'Quiz', icon: <Brain className="w-5 h-5" /> },
    { id: 'culture', label: 'Culture', icon: <Compass className="w-5 h-5" /> },
    { id: 'modules', label: 'Modules', icon: <Settings className="w-5 h-5" /> },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <p className="font-nunito text-xl text-charcoal">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Admin Header */}
      <nav className="bg-charcoal text-white shadow-lg">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-playfair text-2xl font-bold flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Administration Darija
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setLoading(true);
                // Force refresh all data without cache
                Promise.all([
                  axios.get(`${API}/admin/stats`, { 
                    headers: { 
                      Authorization: `Bearer ${token}`,
                      'Cache-Control': 'no-cache',
                      'Pragma': 'no-cache'
                    } 
                  }),
                  axios.get(`${API}/modules`, {
                    headers: {
                      'Cache-Control': 'no-cache',
                      'Pragma': 'no-cache'
                    }
                  })
                ]).then(([statsRes, modulesRes]) => {
                  setStats(statsRes.data);
                  setLevels(modulesRes.data.levels || []);
                  setModules(modulesRes.data.modules || []);
                  toast.success('🔄 Données actualisées !');
                }).catch(() => {
                  toast.error('Erreur lors du rafraîchissement');
                }).finally(() => {
                  setLoading(false);
                });
              }}
              data-testid="refresh-data-btn"
              className="bg-green-600 hover:bg-green-500 text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Rafraîchir
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="preview-app-btn"
              className="bg-oasis hover:bg-oasis/90 text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Eye className="w-5 h-5" />
              Prévisualiser l'app
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard"
              className="bg-stone-600 hover:bg-stone-500 text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Retour
            </button>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-stone-200 min-h-[calc(100vh-72px)] p-4">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`w-full text-left font-nunito py-3 px-4 rounded-lg flex items-center gap-3 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-white font-bold'
                    : 'text-charcoal hover:bg-stone-100'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {activeTab === 'dashboard' && <AdminDashboard stats={stats} levels={levels} />}
          {activeTab === 'settings' && <AppSettingsManager token={token} />}
          {activeTab === 'content-order' && <ContentOrderManager token={token} />}
          {activeTab === 'sounds' && <SoundsManager token={token} onUpdate={fetchData} />}
          {activeTab === 'vocabulary' && <VocabularyManager token={token} levels={levels} onUpdate={fetchData} levelFilter={levelFilter} onClearFilter={() => setLevelFilter(null)} />}
          {activeTab === 'grammar' && <GrammarManager token={token} levels={levels} onUpdate={fetchData} levelFilter={levelFilter} onClearFilter={() => setLevelFilter(null)} />}
          {activeTab === 'mini-quiz' && <GrammarMiniQuizManager token={token} levels={levels} />}
          {activeTab === 'situations' && <SituationManager token={token} levels={levels} onUpdate={fetchData} levelFilter={levelFilter} onClearFilter={() => setLevelFilter(null)} />}
          {activeTab === 'quiz' && <QuizManager token={token} levels={levels} levelFilter={levelFilter} onClearFilter={() => setLevelFilter(null)} />}
          {activeTab === 'culture' && <CultureManager token={token} levels={levels} />}
          {activeTab === 'modules' && <ModuleManager token={token} levels={levels} modules={modules} onUpdate={fetchData} />}
        </main>
      </div>
    </div>
  );
};

// ============ ADMIN DASHBOARD ============
const AdminDashboard = ({ stats, levels }) => {
  const navigate = useNavigate();
  
  return (
    <div>
      <h2 className="font-playfair text-3xl font-bold text-charcoal mb-6">
        Tableau de bord
      </h2>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone-200">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-3 rounded-lg">
              <GraduationCap className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="font-nunito text-3xl font-bold text-charcoal">{stats?.total_vocabulary || 0}</p>
              <p className="font-nunito text-sm text-charcoal/60">Vocabulaire</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone-200">
          <div className="flex items-center gap-4">
            <div className="bg-secondary/10 p-3 rounded-lg">
              <Book className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <p className="font-nunito text-3xl font-bold text-charcoal">{stats?.total_grammar || 0}</p>
              <p className="font-nunito text-sm text-charcoal/60">Grammaire</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone-200">
          <div className="flex items-center gap-4">
            <div className="bg-accent/10 p-3 rounded-lg">
              <Users className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="font-nunito text-3xl font-bold text-charcoal">{stats?.total_situations || 0}</p>
              <p className="font-nunito text-sm text-charcoal/60">Situations</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone-200">
          <div className="flex items-center gap-4">
            <div className="bg-oasis/10 p-3 rounded-lg">
              <Brain className="w-6 h-6 text-oasis" />
            </div>
            <div>
              <p className="font-nunito text-3xl font-bold text-charcoal">{stats?.total_quiz_questions || 0}</p>
              <p className="font-nunito text-sm text-charcoal/60">Quiz</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-stone-200 mb-8">
        <h3 className="font-playfair text-xl font-bold text-charcoal mb-4">Actions rapides</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onClick={() => navigate('/admin?tab=vocabulary')}
            className="bg-primary/10 hover:bg-primary/20 text-primary font-nunito font-semibold py-3 px-4 rounded-lg transition-colors"
          >
            + Ajouter vocabulaire
          </button>
          <button
            onClick={() => navigate('/admin?tab=grammar')}
            className="bg-secondary/10 hover:bg-secondary/20 text-secondary font-nunito font-semibold py-3 px-4 rounded-lg transition-colors"
          >
            + Ajouter grammaire
          </button>
          <button
            onClick={() => navigate('/admin?tab=situations')}
            className="bg-accent/10 hover:bg-accent/20 text-accent font-nunito font-semibold py-3 px-4 rounded-lg transition-colors"
          >
            + Ajouter situation
          </button>
          <button
            onClick={() => navigate('/admin?tab=quiz')}
            className="bg-oasis/10 hover:bg-oasis/20 text-oasis font-nunito font-semibold py-3 px-4 rounded-lg transition-colors"
          >
            + Ajouter quiz
          </button>
        </div>
      </div>

      {/* Levels Overview */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-stone-200">
        <h3 className="font-playfair text-xl font-bold text-charcoal mb-4">Structure des niveaux</h3>
        <div className="space-y-3">
          {levels.map((level) => (
            <div key={level.id} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-2xl">
                  {level.level_number === 1 ? '🌱' : level.level_number === 2 ? '🌿' : '🌳'}
                </span>
                <div>
                  <p className="font-nunito font-bold text-charcoal">{level.title_fr}</p>
                  <p className="font-nunito text-sm text-charcoal/60">{level.description_fr}</p>
                </div>
              </div>
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-nunito">
                Niveau {level.level_number}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ APP SETTINGS MANAGER ============
const AppSettingsManager = ({ token }) => {
  const [counters, setCounters] = useState({
    vocabulary_count: "+1000",
    quiz_count: "+500",
    situations_count: "+100",
    lessons_count: "+50"
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.landing_counters) {
        setCounters(response.data.landing_counters);
      }
    } catch (error) {
      console.error('Error fetching settings');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/settings/landing-counters`, counters, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Compteurs mis à jour !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <h2 className="font-playfair text-3xl font-bold text-charcoal mb-6">
        ⚙️ Paramètres de l'Application
      </h2>

      {/* Landing Page Counters */}
      <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6 mb-6">
        <h3 className="font-playfair text-xl font-bold text-charcoal mb-4">
          📊 Compteurs de la Page d'Accueil
        </h3>
        <p className="font-nunito text-charcoal/60 mb-6">
          Ces compteurs sont affichés sur la page d'accueil pour montrer la richesse du contenu. 
          Modifiez-les selon votre progression.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <label className="block font-nunito font-semibold text-charcoal">
              📚 Vocabulaire
            </label>
            <input
              type="text"
              value={counters.vocabulary_count}
              onChange={(e) => setCounters({ ...counters, vocabulary_count: e.target.value })}
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito text-xl font-bold text-primary text-center"
              placeholder="+1000"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block font-nunito font-semibold text-charcoal">
              🧠 Quiz
            </label>
            <input
              type="text"
              value={counters.quiz_count}
              onChange={(e) => setCounters({ ...counters, quiz_count: e.target.value })}
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito text-xl font-bold text-secondary text-center"
              placeholder="+500"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block font-nunito font-semibold text-charcoal">
              💬 Situations
            </label>
            <input
              type="text"
              value={counters.situations_count}
              onChange={(e) => setCounters({ ...counters, situations_count: e.target.value })}
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito text-xl font-bold text-accent text-center"
              placeholder="+100"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block font-nunito font-semibold text-charcoal">
              🎓 Leçons
            </label>
            <input
              type="text"
              value={counters.lessons_count}
              onChange={(e) => setCounters({ ...counters, lessons_count: e.target.value })}
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito text-xl font-bold text-orange-500 text-center"
              placeholder="+50"
            />
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-primary hover:bg-primary/90 text-white font-nunito font-bold py-3 px-6 rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {isSaving ? 'Sauvegarde...' : 'Sauvegarder les compteurs'}
          </button>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-stone-100 rounded-xl p-6">
        <h4 className="font-nunito font-bold text-charcoal mb-4">👁️ Aperçu sur la page d'accueil :</h4>
        <div className="flex flex-wrap gap-4">
          <div className="bg-white rounded-xl px-4 py-3 shadow border border-primary/20">
            <span className="font-playfair text-2xl font-bold text-primary">{counters.vocabulary_count}</span>
            <p className="font-nunito text-xs text-charcoal/70">Mots</p>
          </div>
          <div className="bg-white rounded-xl px-4 py-3 shadow border border-secondary/20">
            <span className="font-playfair text-2xl font-bold text-secondary">{counters.quiz_count}</span>
            <p className="font-nunito text-xs text-charcoal/70">Quiz</p>
          </div>
          <div className="bg-white rounded-xl px-4 py-3 shadow border border-accent/20">
            <span className="font-playfair text-2xl font-bold text-accent">{counters.situations_count}</span>
            <p className="font-nunito text-xs text-charcoal/70">Situations</p>
          </div>
          <div className="bg-white rounded-xl px-4 py-3 shadow border border-orange-400/20">
            <span className="font-playfair text-2xl font-bold text-orange-500">{counters.lessons_count}</span>
            <p className="font-nunito text-xs text-charcoal/70">Leçons</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ CONTENT ORDER MANAGER (Vocabulary, Grammar, Situations) ============
const ContentOrderManager = ({ token }) => {
  const [selectedLevel, setSelectedLevel] = useState('beginner');
  const [activeSection, setActiveSection] = useState('vocabulary'); // vocabulary, grammar, situations
  const [items, setItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchItems(selectedLevel, activeSection);
  }, [selectedLevel, activeSection]);

  const fetchItems = async (level, section) => {
    setIsLoading(true);
    try {
      let endpoint;
      if (section === 'vocabulary') {
        endpoint = `${API}/admin/categories-by-level/${level}`;
      } else if (section === 'grammar') {
        endpoint = `${API}/admin/grammar-by-level/${level}`;
      } else {
        endpoint = `${API}/admin/situations-by-level/${level}`;
      }
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (section === 'vocabulary') {
        setItems(response.data.categories || []);
      } else {
        setItems(response.data.items || []);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  const moveItem = (index, direction) => {
    const newItems = [...items];
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= newItems.length) return;
    
    // Swap
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    
    // Update order values
    newItems.forEach((item, i) => {
      item.order = i;
    });
    
    setItems(newItems);
  };

  const updateEmoji = (index, emoji) => {
    const newItems = [...items];
    newItems[index].emoji = emoji;
    setItems(newItems);
  };

  const toggleHideTranslation = (index) => {
    const newItems = [...items];
    newItems[index].hide_translation = !newItems[index].hide_translation;
    setItems(newItems);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (activeSection === 'vocabulary') {
        await axios.put(`${API}/admin/category-order`, {
          level: selectedLevel,
          categories: items
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.put(`${API}/admin/content-order`, {
          level: selectedLevel,
          content_type: activeSection,
          items: items.map(item => ({
            id: item.id,
            title: item.title || item.category,
            order: item.order
          }))
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success(`Ordre sauvegardé !`);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const getLevelLabel = (level) => {
    const labels = {
      beginner: '🌱 Niveau 1 - Débutant',
      intermediate: '🌿 Niveau 2 - Intermédiaire',
      advanced: '🌳 Niveau 3 - Avancé'
    };
    return labels[level] || level;
  };

  const getSectionLabel = (section) => {
    const labels = {
      vocabulary: '📚 Catégories Vocabulaire',
      grammar: '📖 Leçons de Grammaire',
      situations: '💬 Situations'
    };
    return labels[section] || section;
  };

  const getItemLabel = (item) => {
    if (activeSection === 'vocabulary') {
      return item.category;
    }
    return item.title;
  };

  const getItemIcon = (item) => {
    if (activeSection === 'vocabulary') {
      return item.emoji || getCategoryIcon(item.category);
    }
    return item.icon || (activeSection === 'grammar' ? '📖' : '💬');
  };

  return (
    <div>
      <h2 className="font-playfair text-3xl font-bold text-charcoal mb-6">
        🔄 Réorganisation du Contenu
      </h2>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="font-nunito text-blue-800">
          <strong>💡 Conseil :</strong> Organisez le contenu dans l'ordre de progression pédagogique. 
          Les éléments en haut apparaîtront en premier dans l'application.
        </p>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'vocabulary', label: '📚 Vocabulaire', color: 'primary' },
          { id: 'grammar', label: '📖 Grammaire', color: 'secondary' },
          { id: 'situations', label: '💬 Situations', color: 'accent' }
        ].map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`px-4 py-2 rounded-lg font-nunito font-semibold transition-colors ${
              activeSection === section.id
                ? section.id === 'vocabulary' ? 'bg-primary text-white' 
                : section.id === 'grammar' ? 'bg-secondary text-white'
                : 'bg-accent text-white'
                : 'bg-stone-100 text-charcoal hover:bg-stone-200'
            }`}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* Level Selector */}
      <div className="flex gap-2 mb-6">
        {['beginner', 'intermediate', 'advanced'].map((level) => (
          <button
            key={level}
            onClick={() => setSelectedLevel(level)}
            className={`px-4 py-2 rounded-lg font-nunito font-semibold transition-colors ${
              selectedLevel === level
                ? 'bg-charcoal text-white'
                : 'bg-stone-100 text-charcoal hover:bg-stone-200'
            }`}
          >
            {getLevelLabel(level)}
          </button>
        ))}
      </div>

      {/* Items List */}
      <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
        <div className="p-4 bg-stone-50 border-b border-stone-200 flex justify-between items-center">
          <h3 className="font-nunito font-bold text-charcoal">
            {getSectionLabel(activeSection)} - {items.length} éléments
          </h3>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-primary hover:bg-primary/90 text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-charcoal/50 font-nunito">
            Chargement...
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-charcoal/50 font-nunito">
            Aucun élément trouvé pour ce niveau. Ajoutez du contenu d'abord.
          </div>
        ) : (
          <div className="divide-y divide-stone-200">
            {items.map((item, index) => (
              <div
                key={item.id || item.category}
                className="flex items-center gap-4 p-4 hover:bg-stone-50"
              >
                {/* Order number */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeSection === 'vocabulary' ? 'bg-primary/10' 
                  : activeSection === 'grammar' ? 'bg-secondary/10'
                  : 'bg-accent/10'
                }`}>
                  <span className={`font-nunito font-bold text-sm ${
                    activeSection === 'vocabulary' ? 'text-primary' 
                    : activeSection === 'grammar' ? 'text-secondary'
                    : 'text-accent'
                  }`}>{index + 1}</span>
                </div>

                {/* Icon/Emoji */}
                {activeSection === 'vocabulary' ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={item.emoji || getCategoryIcon(item.category)}
                      onChange={(e) => updateEmoji(index, e.target.value)}
                      className="w-12 h-10 text-center text-2xl bg-stone-50 border border-stone-200 rounded-lg"
                      maxLength={2}
                    />
                    <button
                      onClick={() => toggleHideTranslation(index)}
                      title={item.hide_translation ? 'Afficher traductions' : 'Masquer traductions'}
                      className={`p-2 rounded-lg border transition-colors ${
                        item.hide_translation 
                          ? 'bg-amber-100 border-amber-300 text-amber-700' 
                          : 'bg-stone-50 border-stone-200 text-stone-400 hover:bg-stone-100'
                      }`}
                    >
                      {item.hide_translation ? '🙈' : '👁️'}
                    </button>
                  </div>
                ) : (
                  <div className="w-12 h-10 flex items-center justify-center text-2xl">
                    {getItemIcon(item)}
                  </div>
                )}

                {/* Item name */}
                <div className="flex-1">
                  <p className="font-nunito font-semibold text-charcoal">{getItemLabel(item)}</p>
                </div>

                {/* Move buttons */}
                <div className="flex gap-1">
                  <button
                    onClick={() => moveItem(index, -1)}
                    disabled={index === 0}
                    className="p-2 bg-stone-100 hover:bg-stone-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Monter"
                  >
                    <ArrowUp className="w-4 h-4 text-charcoal" />
                  </button>
                  <button
                    onClick={() => moveItem(index, 1)}
                    disabled={index === items.length - 1}
                    className="p-2 bg-stone-100 hover:bg-stone-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Descendre"
                  >
                    <ArrowDown className="w-4 h-4 text-charcoal" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============ SOUNDS MANAGER ============
const SoundsManager = ({ token, onUpdate }) => {
  const [sounds, setSounds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    letter: '',
    phonetic: '',
    description_fr: '',
    description_en: '',
    examples_fr: '',
    examples_en: ''
  });

  useEffect(() => {
    fetchSounds();
  }, []);

  const fetchSounds = async () => {
    try {
      const response = await axios.get(`${API}/darija-sounds`);
      setSounds(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des sons');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        letter: formData.letter,
        phonetic: formData.phonetic,
        description_fr: formData.description_fr,
        description_en: formData.description_en,
        examples_fr: formData.examples_fr.split(',').map(s => s.trim()).filter(s => s),
        examples_en: formData.examples_en.split(',').map(s => s.trim()).filter(s => s)
      };

      if (editingId) {
        await axios.put(`${API}/admin/darija-sounds/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Son mis à jour');
      } else {
        await axios.post(`${API}/admin/darija-sounds`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Son ajouté');
      }
      fetchSounds();
      resetForm();
      onUpdate?.();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (sound) => {
    setFormData({
      letter: sound.letter,
      phonetic: sound.phonetic,
      description_fr: sound.description_fr || '',
      description_en: sound.description_en || '',
      examples_fr: (sound.examples_fr || []).join(', '),
      examples_en: (sound.examples_en || []).join(', ')
    });
    setEditingId(sound.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce son ?')) return;
    try {
      await axios.delete(`${API}/admin/darija-sounds/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Son supprimé');
      fetchSounds();
      onUpdate?.();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      letter: '',
      phonetic: '',
      description_fr: '',
      description_en: '',
      examples_fr: '',
      examples_en: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleAudioSaved = (soundId, audioUrl, audioType) => {
    if (audioType === 'sound_example') {
      setSounds(prev => prev.map(s => 
        s.id === soundId ? { ...s, audio_example_url: audioUrl } : s
      ));
    } else {
      setSounds(prev => prev.map(s => 
        s.id === soundId ? { ...s, audio_url: audioUrl } : s
      ));
    }
    fetchSounds(); // Refresh to get updated data
    toast.success('Audio enregistré !');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-playfair text-3xl font-bold text-charcoal">
          Sons de la Darija (Introduction)
        </h2>
        <button
          onClick={() => setShowForm(true)}
          data-testid="add-sound-btn"
          className="bg-primary text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2 hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ajouter un son
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-stone-200 mb-6"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-nunito font-semibold text-charcoal mb-1">Lettre / Son *</label>
                <input
                  type="text"
                  value={formData.letter}
                  onChange={(e) => setFormData({ ...formData, letter: e.target.value })}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 font-nunito focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  placeholder="Ex: ع (Ayn)"
                  required
                />
              </div>
              <div>
                <label className="block font-nunito font-semibold text-charcoal mb-1">Phonétique *</label>
                <input
                  type="text"
                  value={formData.phonetic}
                  onChange={(e) => setFormData({ ...formData, phonetic: e.target.value })}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 font-nunito focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  placeholder="Ex: 3"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block font-nunito font-semibold text-charcoal mb-1">Description (FR) *</label>
              <textarea
                value={formData.description_fr}
                onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
                className="w-full border border-stone-300 rounded-lg px-3 py-2 font-nunito focus:ring-2 focus:ring-primary/50 focus:border-primary"
                rows="2"
                placeholder="Comment prononcer ce son..."
                required
              />
            </div>
            
            <div>
              <label className="block font-nunito font-semibold text-charcoal mb-1">Description (EN)</label>
              <textarea
                value={formData.description_en}
                onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                className="w-full border border-stone-300 rounded-lg px-3 py-2 font-nunito focus:ring-2 focus:ring-primary/50 focus:border-primary"
                rows="2"
                placeholder="How to pronounce this sound..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-nunito font-semibold text-charcoal mb-1">Exemples (FR, séparés par virgule)</label>
                <input
                  type="text"
                  value={formData.examples_fr}
                  onChange={(e) => setFormData({ ...formData, examples_fr: e.target.value })}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 font-nunito focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  placeholder="3andi, 3afak, 3la"
                />
              </div>
              <div>
                <label className="block font-nunito font-semibold text-charcoal mb-1">Exemples (EN, séparés par virgule)</label>
                <input
                  type="text"
                  value={formData.examples_en}
                  onChange={(e) => setFormData({ ...formData, examples_en: e.target.value })}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 font-nunito focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  placeholder="3andi (I have), 3afak (please)"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                className="bg-primary text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2 hover:bg-primary/90"
              >
                <Save className="w-5 h-5" />
                {editingId ? 'Mettre à jour' : 'Ajouter'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-stone-200 text-charcoal font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2 hover:bg-stone-300"
              >
                <X className="w-5 h-5" />
                Annuler
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Sounds List */}
      <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="text-left font-nunito font-bold text-charcoal py-3 px-4">Lettre</th>
              <th className="text-left font-nunito font-bold text-charcoal py-3 px-4">Phonétique</th>
              <th className="text-left font-nunito font-bold text-charcoal py-3 px-4">Description</th>
              <th className="text-center font-nunito font-bold text-charcoal py-3 px-4">🔊 Son Isolé</th>
              <th className="text-center font-nunito font-bold text-charcoal py-3 px-4">📝 Son en Mot</th>
              <th className="text-right font-nunito font-bold text-charcoal py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200">
            {sounds.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-8 font-nunito text-charcoal/60">
                  Aucun son ajouté. Cliquez sur "Ajouter un son" pour commencer.
                </td>
              </tr>
            ) : (
              sounds.map((sound) => (
                <tr key={sound.id} className="hover:bg-stone-50">
                  <td className="py-3 px-4">
                    <span className="font-bold text-primary text-lg">{sound.letter}</span>
                  </td>
                  <td className="py-3 px-4 font-nunito text-charcoal">{sound.phonetic}</td>
                  <td className="py-3 px-4 font-nunito text-charcoal/70 text-sm max-w-xs truncate">
                    {sound.description_fr}
                  </td>
                  {/* Son Isolé */}
                  <td className="py-3 px-4">
                    <div className="flex flex-col items-center gap-1">
                      <AudioRecorder
                        itemType="sound"
                        itemId={sound.id}
                        token={token}
                        currentAudioUrl={sound.audio_url}
                        onAudioSaved={(url) => handleAudioSaved(sound.id, url, 'sound')}
                        compact
                      />
                    </div>
                  </td>
                  {/* Son en Mot */}
                  <td className="py-3 px-4">
                    <div className="flex flex-col items-center gap-1">
                      <AudioRecorder
                        itemType="sound_example"
                        itemId={sound.id}
                        token={token}
                        currentAudioUrl={sound.audio_example_url}
                        onAudioSaved={(url) => handleAudioSaved(sound.id, url, 'sound_example')}
                        compact
                      />
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(sound)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(sound.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============ VOCABULARY MANAGER (Updated with level dropdown) ============
const VocabularyManager = ({ token, levels, onUpdate, levelFilter, onClearFilter }) => {
  const [vocabulary, setVocabulary] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // Search field
  const [formData, setFormData] = useState({
    word_darija_phonetic: '',
    word_darija_arabic: '',
    word_french: '',
    word_english: '',
    category: '',
    category_icon: '',
    level: 'beginner',
    module_id: '',
    image_url: ''
  });

  useEffect(() => {
    fetchVocabulary();
  }, []);

  const fetchVocabulary = async () => {
    try {
      // Force fresh data - bypass browser cache completely
      const response = await freshFetch(`${API}/vocabulary`);
      setVocabulary(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData };
      if (!dataToSend.module_id) delete dataToSend.module_id;
      
      if (editingId) {
        await axios.put(`${API}/admin/vocabulary/${editingId}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vocabulaire modifié!');
      } else {
        await axios.post(`${API}/admin/vocabulary`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vocabulaire ajouté!');
      }
      resetForm();
      fetchVocabulary();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce mot?')) return;
    try {
      await axios.delete(`${API}/admin/vocabulary/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vocabulaire supprimé!');
      fetchVocabulary();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (vocab) => {
    setEditingId(vocab.id);
    setFormData({
      word_darija_phonetic: vocab.word_darija_phonetic,
      word_darija_arabic: vocab.word_darija_arabic,
      word_french: vocab.word_french,
      word_english: vocab.word_english,
      category: vocab.category,
      category_icon: vocab.category_icon || '',
      level: vocab.level,
      module_id: vocab.module_id || '',
      image_url: vocab.image_url || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      word_darija_phonetic: '',
      word_darija_arabic: '',
      word_french: '',
      word_english: '',
      category: '',
      category_icon: '',
      level: 'beginner',
      module_id: '',
      image_url: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getLevelLabel = (level) => {
    const labels = { beginner: 'Niveau 1 - Débutant', intermediate: 'Niveau 2 - Intermédiaire', advanced: 'Niveau 3 - Avancé' };
    return labels[level] || level;
  };

  // Filter vocabulary by level and search query
  const filteredVocabulary = vocabulary.filter(v => {
    const matchesLevel = !levelFilter || v.level === levelFilter;
    const matchesSearch = !searchQuery || 
      v.word_french?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.word_darija_phonetic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.word_english?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.category?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesLevel && matchesSearch;
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h2 className="font-playfair text-3xl font-bold text-charcoal">Vocabulaire</h2>
          <span className="bg-stone-200 text-charcoal px-3 py-1 rounded-full text-sm font-nunito font-semibold">
            {vocabulary.length} mots
          </span>
          {levelFilter && (
            <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-nunito font-semibold flex items-center gap-2">
              Filtré: {getLevelLabel(levelFilter)}
              <button onClick={onClearFilter} className="hover:text-primary/70">✕</button>
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              toast.info('🔄 Actualisation...');
              fetchVocabulary();
            }}
            data-testid="refresh-vocabulary-btn"
            className="bg-green-600 hover:bg-green-500 text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            Rafraîchir
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            data-testid="add-vocabulary-btn"
            className="bg-primary hover:bg-primary/90 text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Ajouter un mot
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="🔍 Rechercher un mot (français, darija, catégorie...)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        {searchQuery && (
          <p className="mt-2 text-sm text-charcoal/60 font-nunito">
            {filteredVocabulary.length} résultat(s) trouvé(s) pour "{searchQuery}"
          </p>
        )}
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-stone-200"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-playfair text-xl font-bold text-charcoal">
              {editingId ? 'Modifier' : 'Ajouter'} un mot
            </h3>
            <button onClick={resetForm} className="text-charcoal/50 hover:text-charcoal">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Darija (phonétique)"
                value={formData.word_darija_phonetic}
                onChange={(e) => setFormData({ ...formData, word_darija_phonetic: e.target.value })}
                required
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              <input
                type="text"
                placeholder="Darija (arabe)"
                value={formData.word_darija_arabic}
                onChange={(e) => setFormData({ ...formData, word_darija_arabic: e.target.value })}
                required
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-amiri focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              <input
                type="text"
                placeholder="Français"
                value={formData.word_french}
                onChange={(e) => setFormData({ ...formData, word_french: e.target.value })}
                required
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              <input
                type="text"
                placeholder="English"
                value={formData.word_english}
                onChange={(e) => setFormData({ ...formData, word_english: e.target.value })}
                required
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-nunito text-charcoal/70 mb-1">Catégorie + Emoji</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Catégorie (ex: salutations)"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    className="flex-1 bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <EmojiPicker
                    value={formData.category_icon || ''}
                    onChange={(emoji) => setFormData({ ...formData, category_icon: emoji })}
                    placeholder="📚"
                  />
                </div>
              </div>
              <select
                value={formData.level}
                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="beginner">🌱 Niveau 1 - Débutant</option>
                <option value="intermediate">🌿 Niveau 2 - Intermédiaire</option>
                <option value="advanced">🌳 Niveau 3 - Avancé</option>
              </select>
            </div>
            
            {/* Champ URL Image pour mode Immersion */}
            <div>
              <label className="block text-sm font-nunito text-charcoal/70 mb-1">
                🖼️ URL de l'image (optionnel - pour mode Immersion: Couleurs, Nourriture, Vêtements, etc.)
              </label>
              <input
                type="url"
                placeholder="https://exemple.com/image.jpg"
                value={formData.image_url || ''}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              {formData.image_url && (
                <div className="mt-2 rounded-lg overflow-hidden bg-stone-100 inline-block">
                  <img 
                    src={formData.image_url} 
                    alt="Aperçu" 
                    referrerPolicy="no-referrer"
                    crossOrigin="anonymous"
                    className="h-20 object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>
            
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                className="bg-secondary hover:bg-secondary/90 text-white font-nunito font-bold py-2 px-6 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Save className="w-5 h-5" />
                Sauvegarder
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-stone-200 hover:bg-stone-300 text-charcoal font-nunito font-bold py-2 px-6 rounded-lg transition-colors"
              >
                Annuler
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Vocabulary Table */}
      <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="text-left font-nunito font-bold text-charcoal px-4 py-3">Darija</th>
              <th className="text-left font-nunito font-bold text-charcoal px-4 py-3">Traductions</th>
              <th className="text-left font-nunito font-bold text-charcoal px-4 py-3">Catégorie</th>
              <th className="text-left font-nunito font-bold text-charcoal px-4 py-3">Niveau</th>
              <th className="text-left font-nunito font-bold text-charcoal px-4 py-3">Image</th>
              <th className="text-left font-nunito font-bold text-charcoal px-4 py-3">Audio</th>
              <th className="text-right font-nunito font-bold text-charcoal px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredVocabulary.map((vocab) => (
              <tr key={vocab.id} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="px-4 py-3">
                  <p className="font-nunito font-bold text-primary">{vocab.word_darija_phonetic}</p>
                  <p className="font-amiri text-sm text-charcoal/70">{vocab.word_darija_arabic}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="font-nunito text-sm">{vocab.word_french}</p>
                  <p className="font-nunito text-sm text-charcoal/60">{vocab.word_english}</p>
                </td>
                <td className="px-4 py-3">
                  <span className="bg-accent/10 text-accent px-2 py-1 rounded text-sm font-nunito">
                    {vocab.category}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="bg-primary/10 text-primary px-2 py-1 rounded text-sm font-nunito">
                    {vocab.level === 'beginner' ? '🌱 N1' : vocab.level === 'intermediate' ? '🌿 N2' : '🌳 N3'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {vocab.image_url ? (
                    <img 
                      src={vocab.image_url} 
                      alt={vocab.word_french}
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      className="w-12 h-12 object-cover rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ) : (
                    <span className="text-stone-400 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <AudioRecorder 
                    itemType="vocabulary"
                    itemId={vocab.id}
                    currentAudioUrl={vocab.audio_url}
                    token={token}
                    onAudioSaved={() => fetchVocabulary()}
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleEdit(vocab)}
                      className="p-2 text-secondary hover:bg-secondary/10 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(vocab.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredVocabulary.length === 0 && (
          <div className="text-center py-12 text-charcoal/50 font-nunito">
            Aucun vocabulaire. Cliquez sur "Ajouter un mot" pour commencer.
          </div>
        )}
      </div>
    </div>
  );
};

// ============ GRAMMAR MANAGER ============
const GrammarManager = ({ token, levels, onUpdate, levelFilter, onClearFilter }) => {
  const [grammar, setGrammar] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    icon: '',
    explanation: '',
    examples: [{ darija_phonetic: '', darija_arabic: '', french: '', english: '' }],
    level: 'beginner',
    module_id: ''
  });

  useEffect(() => {
    fetchGrammar();
  }, []);

  const fetchGrammar = async () => {
    try {
      const response = await axios.get(`${API}/grammar`);
      setGrammar(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData };
      if (!dataToSend.module_id) delete dataToSend.module_id;
      
      if (editingId) {
        await axios.put(`${API}/admin/grammar/${editingId}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Fiche modifiée!');
      } else {
        await axios.post(`${API}/admin/grammar`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Fiche ajoutée!');
      }
      resetForm();
      fetchGrammar();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette fiche?')) return;
    try {
      await axios.delete(`${API}/admin/grammar/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fiche supprimée!');
      fetchGrammar();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (g) => {
    setEditingId(g.id);
    setFormData({
      title: g.title,
      icon: g.icon,
      explanation: g.explanation,
      examples: g.examples || [{ darija_phonetic: '', darija_arabic: '', french: '', english: '' }],
      level: g.level || 'beginner',
      module_id: g.module_id || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      icon: '',
      explanation: '',
      examples: [{ darija_phonetic: '', darija_arabic: '', french: '', english: '' }],
      level: 'beginner',
      module_id: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const addExample = () => {
    setFormData({
      ...formData,
      examples: [...formData.examples, { darija_phonetic: '', darija_arabic: '', french: '', english: '' }]
    });
  };

  const updateExample = (index, field, value) => {
    const newExamples = [...formData.examples];
    newExamples[index][field] = value;
    setFormData({ ...formData, examples: newExamples });
  };

  const removeExample = (index) => {
    if (formData.examples.length > 1) {
      setFormData({
        ...formData,
        examples: formData.examples.filter((_, i) => i !== index)
      });
    }
  };

  const getLevelLabel = (level) => {
    const labels = { beginner: 'Niveau 1 - Débutant', intermediate: 'Niveau 2 - Intermédiaire', advanced: 'Niveau 3 - Avancé' };
    return labels[level] || level;
  };

  // Filter grammar by level
  const filteredGrammar = levelFilter 
    ? grammar.filter(g => g.level === levelFilter)
    : grammar;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h2 className="font-playfair text-3xl font-bold text-charcoal">Grammaire</h2>
          {levelFilter && (
            <span className="bg-secondary/10 text-secondary px-3 py-1 rounded-full text-sm font-nunito font-semibold flex items-center gap-2">
              Filtré: {getLevelLabel(levelFilter)}
              <button onClick={onClearFilter} className="hover:text-secondary/70">✕</button>
            </span>
          )}
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary hover:bg-primary/90 text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ajouter une fiche
        </button>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-stone-200"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-playfair text-xl font-bold text-charcoal">
              {editingId ? 'Modifier' : 'Ajouter'} une fiche de grammaire
            </h3>
            <button onClick={resetForm} className="text-charcoal/50 hover:text-charcoal">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Titre (ex: Mon / Ma / Mes)"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
              />
              <EmojiPicker
                value={formData.icon}
                onChange={(emoji) => setFormData({ ...formData, icon: emoji })}
                placeholder="Icône (emoji)"
              />
              <select
                value={formData.level}
                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
              >
                <option value="beginner">🌱 Niveau 1 - Débutant</option>
                <option value="intermediate">🌿 Niveau 2 - Intermédiaire</option>
                <option value="advanced">🌳 Niveau 3 - Avancé</option>
              </select>
            </div>
            <textarea
              placeholder="Explication de la règle (supporte Markdown)"
              value={formData.explanation}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              required
              rows={6}
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
            />
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm font-nunito text-blue-800">
              <p className="font-bold mb-1">💡 Support Markdown :</p>
              <p>**gras**, - liste à puces, | Colonne 1 | Colonne 2 | pour tableaux</p>
            </div>

            <h4 className="font-nunito font-bold text-charcoal pt-2">Exemples:</h4>
            {formData.examples.map((example, index) => (
              <div key={index} className="border border-stone-200 rounded-lg p-4 space-y-2 relative">
                {formData.examples.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeExample(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Phonétique"
                    value={example.darija_phonetic}
                    onChange={(e) => updateExample(index, 'darija_phonetic', e.target.value)}
                    className="w-full bg-stone-50 border border-stone-300 rounded-lg px-3 py-2 font-nunito text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Arabe"
                    value={example.darija_arabic}
                    onChange={(e) => updateExample(index, 'darija_arabic', e.target.value)}
                    className="w-full bg-stone-50 border border-stone-300 rounded-lg px-3 py-2 font-amiri text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Français"
                    value={example.french}
                    onChange={(e) => updateExample(index, 'french', e.target.value)}
                    className="w-full bg-stone-50 border border-stone-300 rounded-lg px-3 py-2 font-nunito text-sm"
                  />
                  <input
                    type="text"
                    placeholder="English"
                    value={example.english}
                    onChange={(e) => updateExample(index, 'english', e.target.value)}
                    className="w-full bg-stone-50 border border-stone-300 rounded-lg px-3 py-2 font-nunito text-sm"
                  />
                </div>
              </div>
            ))}
            
            <button type="button" onClick={addExample} className="text-primary font-nunito font-semibold hover:underline">
              + Ajouter un exemple
            </button>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                className="bg-secondary hover:bg-secondary/90 text-white font-nunito font-bold py-2 px-6 rounded-lg flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                Sauvegarder
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-stone-200 hover:bg-stone-300 text-charcoal font-nunito font-bold py-2 px-6 rounded-lg"
              >
                Annuler
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Grammar Cards with Examples */}
      <div className="space-y-4">
        {filteredGrammar.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-stone-200 text-center py-12 text-charcoal/50 font-nunito">
            {levelFilter ? 'Aucune fiche de grammaire pour ce niveau.' : 'Aucune fiche de grammaire. Cliquez sur "Ajouter une fiche" pour commencer.'}
          </div>
        ) : (
          filteredGrammar.map((g) => (
            <div key={g.id} className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
              {/* Header */}
              <div className="bg-stone-50 border-b border-stone-200 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{g.icon}</span>
                  <div>
                    <h4 className="font-nunito font-bold text-charcoal">{g.title}</h4>
                    <span className="bg-primary/10 text-primary px-2 py-0.5 rounded text-xs font-nunito">
                      {g.level === 'beginner' ? '🌱 N1' : g.level === 'intermediate' ? '🌿 N2' : '🌳 N3'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(g)}
                    className="p-2 text-secondary hover:bg-secondary/10 rounded-lg"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(g.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Examples with individual audio recorders */}
              <div className="p-4">
                <p className="font-nunito text-sm text-charcoal/60 mb-3">
                  🎤 Enregistrez l'audio pour chaque exemple en Darija :
                </p>
                <div className="space-y-2">
                  {(g.examples || []).map((example, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-sand/50 rounded-lg border border-stone-200">
                      <div className="flex-1">
                        <p className="font-nunito font-bold text-primary">
                          {example.darija_phonetic}
                        </p>
                        <p className="font-nunito text-sm text-charcoal/60">
                          {example.french}
                        </p>
                      </div>
                      <AudioRecorder 
                        itemType="grammar_example"
                        itemId={g.id}
                        exampleIndex={idx}
                        currentAudioUrl={example.audio_url}
                        token={token}
                        onAudioSaved={() => fetchGrammar()}
                        compact={true}
                      />
                    </div>
                  ))}
                  {(!g.examples || g.examples.length === 0) && (
                    <p className="text-charcoal/40 font-nunito text-sm italic">
                      Aucun exemple. Modifiez la fiche pour ajouter des exemples.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ============ SITUATION MANAGER ============
const SituationManager = ({ token, levels, onUpdate, levelFilter, onClearFilter }) => {
  const [situations, setSituations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    title_en: '',  // English title for Grand Quiz EN mode
    icon: '',
    phrases: [{ darija_phonetic: '', darija_arabic: '', french: '', english: '' }],
    level: 'beginner',
    module_id: ''
  });

  useEffect(() => {
    fetchSituations();
  }, []);

  const fetchSituations = async () => {
    try {
      // Force fresh data - bypass browser cache
      const response = await freshFetch(`${API}/situations`);
      setSituations(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData };
      if (!dataToSend.module_id) delete dataToSend.module_id;
      
      if (editingId) {
        await axios.put(`${API}/admin/situations/${editingId}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Situation modifiée!');
      } else {
        await axios.post(`${API}/admin/situations`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Situation ajoutée!');
      }
      resetForm();
      fetchSituations();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette situation?')) return;
    try {
      await axios.delete(`${API}/admin/situations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Situation supprimée!');
      fetchSituations();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (s) => {
    setEditingId(s.id);
    setFormData({
      title: s.title,
      title_en: s.title_en || '',
      icon: s.icon,
      phrases: s.phrases || [{ darija_phonetic: '', darija_arabic: '', french: '', english: '' }],
      level: s.level || 'beginner',
      module_id: s.module_id || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      title_en: '',
      icon: '',
      phrases: [{ darija_phonetic: '', darija_arabic: '', french: '', english: '' }],
      level: 'beginner',
      module_id: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const addPhrase = () => {
    setFormData({
      ...formData,
      phrases: [...formData.phrases, { darija_phonetic: '', darija_arabic: '', french: '', english: '' }]
    });
  };

  const updatePhrase = (index, field, value) => {
    const newPhrases = [...formData.phrases];
    newPhrases[index][field] = value;
    setFormData({ ...formData, phrases: newPhrases });
  };

  const removePhrase = (index) => {
    if (formData.phrases.length > 1) {
      setFormData({
        ...formData,
        phrases: formData.phrases.filter((_, i) => i !== index)
      });
    }
  };

  const getLevelLabel = (level) => {
    const labels = { beginner: 'Niveau 1 - Débutant', intermediate: 'Niveau 2 - Intermédiaire', advanced: 'Niveau 3 - Avancé' };
    return labels[level] || level;
  };

  // Filter situations by level
  const filteredSituations = levelFilter 
    ? situations.filter(s => s.level === levelFilter)
    : situations;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h2 className="font-playfair text-3xl font-bold text-charcoal">Situations</h2>
          <span className="bg-stone-200 text-charcoal px-3 py-1 rounded-full text-sm font-nunito font-semibold">
            {situations.length} situations
          </span>
          {levelFilter && (
            <span className="bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-nunito font-semibold flex items-center gap-2">
              Filtré: {getLevelLabel(levelFilter)}
              <button onClick={onClearFilter} className="hover:text-accent/70">✕</button>
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              toast.info('🔄 Actualisation...');
              fetchSituations();
            }}
            className="bg-green-600 hover:bg-green-500 text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            Rafraîchir
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-primary hover:bg-primary/90 text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Ajouter une situation
          </button>
        </div>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-stone-200"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-playfair text-xl font-bold text-charcoal">
              {editingId ? 'Modifier' : 'Ajouter'} une situation
            </h3>
            <button onClick={resetForm} className="text-charcoal/50 hover:text-charcoal">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Titre FR (ex: Au Taxi)"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
              />
              <input
                type="text"
                placeholder="Title EN (ex: At the Taxi)"
                value={formData.title_en}
                onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <EmojiPicker
                value={formData.icon}
                onChange={(emoji) => setFormData({ ...formData, icon: emoji })}
                placeholder="Icône (emoji)"
              />
              <select
                value={formData.level}
                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
              >
                <option value="beginner">🌱 Niveau 1 - Débutant</option>
                <option value="intermediate">🌿 Niveau 2 - Intermédiaire</option>
                <option value="advanced">🌳 Niveau 3 - Avancé</option>
              </select>
            </div>

            <h4 className="font-nunito font-bold text-charcoal pt-2">Phrases:</h4>
            {formData.phrases.map((phrase, index) => (
              <div key={index} className="border border-stone-200 rounded-lg p-4 space-y-2 relative">
                {formData.phrases.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePhrase(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Phonétique"
                    value={phrase.darija_phonetic}
                    onChange={(e) => updatePhrase(index, 'darija_phonetic', e.target.value)}
                    className="w-full bg-stone-50 border border-stone-300 rounded-lg px-3 py-2 font-nunito text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Arabe"
                    value={phrase.darija_arabic}
                    onChange={(e) => updatePhrase(index, 'darija_arabic', e.target.value)}
                    className="w-full bg-stone-50 border border-stone-300 rounded-lg px-3 py-2 font-amiri text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Français"
                    value={phrase.french}
                    onChange={(e) => updatePhrase(index, 'french', e.target.value)}
                    className="w-full bg-stone-50 border border-stone-300 rounded-lg px-3 py-2 font-nunito text-sm"
                  />
                  <input
                    type="text"
                    placeholder="English"
                    value={phrase.english}
                    onChange={(e) => updatePhrase(index, 'english', e.target.value)}
                    className="w-full bg-stone-50 border border-stone-300 rounded-lg px-3 py-2 font-nunito text-sm"
                  />
                </div>
              </div>
            ))}
            
            <button type="button" onClick={addPhrase} className="text-primary font-nunito font-semibold hover:underline">
              + Ajouter une phrase
            </button>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                className="bg-secondary hover:bg-secondary/90 text-white font-nunito font-bold py-2 px-6 rounded-lg flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                Sauvegarder
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-stone-200 hover:bg-stone-300 text-charcoal font-nunito font-bold py-2 px-6 rounded-lg"
              >
                Annuler
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Situations Cards with Phrases */}
      <div className="space-y-4">
        {filteredSituations.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-stone-200 text-center py-12 text-charcoal/50 font-nunito">
            {levelFilter ? 'Aucune situation pour ce niveau.' : 'Aucune situation. Cliquez sur "Ajouter une situation" pour commencer.'}
          </div>
        ) : (
          filteredSituations.map((s) => (
            <div key={s.id} className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
              {/* Header */}
              <div className="bg-stone-50 border-b border-stone-200 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{s.icon}</span>
                  <div>
                    <h4 className="font-nunito font-bold text-charcoal">{s.title}</h4>
                    <span className="bg-primary/10 text-primary px-2 py-0.5 rounded text-xs font-nunito">
                      {s.level === 'beginner' ? '🌱 N1' : s.level === 'intermediate' ? '🌿 N2' : '🌳 N3'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(s)}
                    className="p-2 text-secondary hover:bg-secondary/10 rounded-lg"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(s.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Phrases with individual audio recorders */}
              <div className="p-4">
                <p className="font-nunito text-sm text-charcoal/60 mb-3">
                  🎤 Enregistrez l'audio pour chaque phrase du dialogue :
                </p>
                <div className="space-y-2">
                  {(s.phrases || []).map((phrase, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-sand/50 rounded-lg border border-stone-200">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-nunito font-bold text-primary">
                          {phrase.darija_phonetic}
                        </p>
                        <p className="font-nunito text-sm text-charcoal/60">
                          {phrase.french}
                        </p>
                      </div>
                      <AudioRecorder 
                        itemType="situation_phrase"
                        itemId={s.id}
                        exampleIndex={idx}
                        currentAudioUrl={phrase.audio_url}
                        token={token}
                        onAudioSaved={() => fetchSituations()}
                        compact={true}
                      />
                    </div>
                  ))}
                  {(!s.phrases || s.phrases.length === 0) && (
                    <p className="text-charcoal/40 font-nunito text-sm italic">
                      Aucune phrase. Modifiez la situation pour ajouter des phrases.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ============ QUIZ MANAGER ============
const QuizManager = ({ token, levels, levelFilter, onClearFilter }) => {
  const [questions, setQuestions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeQuizTab, setActiveQuizTab] = useState('grand_quiz'); // 'grand_quiz' or 'category'
  const [uploadingImage, setUploadingImage] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    options: ['', '', '', ''],
    correct_answer: '',
    category: 'vocabulaire',
    level: 'beginner',
    question_type: 'mcq',
    is_grand_quiz: true,
    blank_sentence: ''
  });

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/quiz`, { params: { limit: 100 } });
      setQuestions(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        is_grand_quiz: activeQuizTab === 'grand_quiz'
      };
      
      if (editingId) {
        await axios.put(`${API}/admin/quiz/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Question modifiée!');
      } else {
        await axios.post(`${API}/admin/quiz`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Question ajoutée!');
      }
      resetForm();
      fetchQuestions();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette question?')) return;
    try {
      await axios.delete(`${API}/admin/quiz/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Question supprimée!');
      fetchQuestions();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (q) => {
    setEditingId(q.id);
    setFormData({
      question: q.question,
      options: q.options || ['', '', '', ''],
      correct_answer: q.correct_answer,
      category: q.category || 'vocabulaire',
      level: q.level,
      question_type: q.question_type || 'mcq',
      is_grand_quiz: q.is_grand_quiz || false,
      blank_sentence: q.blank_sentence || ''
    });
    setShowForm(true);
    setActiveQuizTab(q.is_grand_quiz ? 'grand_quiz' : 'category');
  };

  const resetForm = () => {
    setFormData({
      question: '',
      options: ['', '', '', ''],
      correct_answer: '',
      category: 'vocabulaire',
      level: 'beginner',
      question_type: 'mcq',
      is_grand_quiz: activeQuizTab === 'grand_quiz',
      blank_sentence: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const getLevelLabel = (level) => {
    const labels = { beginner: 'Niveau 1 - Débutant', intermediate: 'Niveau 2 - Intermédiaire', advanced: 'Niveau 3 - Avancé' };
    return labels[level] || level;
  };

  const handleImageUpload = async (e, questionId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(questionId);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('item_type', 'quiz');
    formDataUpload.append('item_id', questionId);
    
    try {
      await axios.post(`${API}/admin/upload/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Image uploadée!');
      fetchQuestions();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingImage(null);
    }
  };

  // Filter questions based on tab and level
  const grandQuizQuestions = questions.filter(q => q.is_grand_quiz);
  const categoryQuestions = questions.filter(q => !q.is_grand_quiz);
  
  const filteredQuestions = activeQuizTab === 'grand_quiz' 
    ? (levelFilter ? grandQuizQuestions.filter(q => q.level === levelFilter) : grandQuizQuestions)
    : (levelFilter ? categoryQuestions.filter(q => q.level === levelFilter) : categoryQuestions);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h2 className="font-playfair text-3xl font-bold text-charcoal">Quiz</h2>
          {levelFilter && (
            <span className="bg-oasis/10 text-oasis px-3 py-1 rounded-full text-sm font-nunito font-semibold flex items-center gap-2">
              Filtré: {getLevelLabel(levelFilter)}
              <button onClick={onClearFilter} className="hover:text-oasis/70">✕</button>
            </span>
          )}
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setFormData(prev => ({ ...prev, is_grand_quiz: activeQuizTab === 'grand_quiz' }));
          }}
          className="bg-primary hover:bg-primary/90 text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Ajouter une question
        </button>
      </div>

      {/* Tabs for Grand Quiz vs Category Quiz */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveQuizTab('grand_quiz')}
          className={`px-4 py-2 rounded-lg font-nunito font-semibold flex items-center gap-2 transition-colors ${
            activeQuizTab === 'grand_quiz' 
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' 
              : 'bg-stone-100 text-charcoal hover:bg-stone-200'
          }`}
        >
          🏆 Grand Quiz ({grandQuizQuestions.length})
        </button>
        <button
          onClick={() => setActiveQuizTab('category')}
          className={`px-4 py-2 rounded-lg font-nunito font-semibold flex items-center gap-2 transition-colors ${
            activeQuizTab === 'category' 
              ? 'bg-primary text-white' 
              : 'bg-stone-100 text-charcoal hover:bg-stone-200'
          }`}
        >
          📚 Quiz Catégories ({categoryQuestions.length})
        </button>
      </div>

      {/* Info box for Grand Quiz */}
      {activeQuizTab === 'grand_quiz' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="font-nunito text-amber-800">
            <strong>🏆 Grand Quiz :</strong> Ces questions sont utilisées dans le quiz final de niveau. 
            Vous pouvez créer des QCM ou des "phrases à trous" pour tester vocabulaire, grammaire et situations.
            Les questions non ajoutées seront générées automatiquement depuis le vocabulaire.
          </p>
        </div>
      )}

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-stone-200"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-playfair text-xl font-bold text-charcoal">
              {editingId ? 'Modifier' : 'Ajouter'} une question {activeQuizTab === 'grand_quiz' ? '(Grand Quiz)' : '(Catégorie)'}
            </h3>
            <button onClick={resetForm} className="text-charcoal/50 hover:text-charcoal">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Question Type Selector */}
            <div>
              <label className="block font-nunito font-semibold text-charcoal mb-2">Type de question:</label>
              <div className="flex gap-3">
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border-2 transition-colors ${
                  formData.question_type === 'mcq' ? 'border-primary bg-primary/10' : 'border-stone-200 hover:border-stone-300'
                }`}>
                  <input
                    type="radio"
                    name="question_type"
                    value="mcq"
                    checked={formData.question_type === 'mcq'}
                    onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
                    className="hidden"
                  />
                  <span className="font-nunito">📝 QCM</span>
                </label>
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border-2 transition-colors ${
                  formData.question_type === 'fill_blank' ? 'border-secondary bg-secondary/10' : 'border-stone-200 hover:border-stone-300'
                }`}>
                  <input
                    type="radio"
                    name="question_type"
                    value="fill_blank"
                    checked={formData.question_type === 'fill_blank'}
                    onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
                    className="hidden"
                  />
                  <span className="font-nunito">✏️ Phrase à trous</span>
                </label>
              </div>
            </div>

            <input
              type="text"
              placeholder="Question (ex: Que signifie 'salam' ?)"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              required
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
            />
            
            {/* Blank sentence field for fill_blank type */}
            {formData.question_type === 'fill_blank' && (
              <div>
                <label className="block font-nunito font-semibold text-charcoal mb-1">
                  Phrase à compléter (utilisez ___ pour le trou):
                </label>
                <input
                  type="text"
                  placeholder="Ex: Ana ___ Marrakech (Je suis de Marrakech)"
                  value={formData.blank_sentence}
                  onChange={(e) => setFormData({ ...formData, blank_sentence: e.target.value })}
                  className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
                />
                <p className="text-sm text-charcoal/50 mt-1 font-nunito">
                  💡 Exemple: "Had ___ dyali" avec options "dar/ktab/mochkil" et réponse "dar"
                </p>
              </div>
            )}

            <div className="space-y-2">
              <label className="font-nunito font-semibold text-charcoal">Options (4 choix):</label>
              {formData.options.map((opt, i) => (
                <input
                  key={i}
                  type="text"
                  placeholder={`Option ${i + 1}${i === 0 ? ' (mettez la bonne réponse ici aussi)' : ''}`}
                  value={opt}
                  onChange={(e) => updateOption(i, e.target.value)}
                  required
                  className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-2 font-nunito"
                />
              ))}
            </div>
            <input
              type="text"
              placeholder="Réponse correcte (doit correspondre à une option)"
              value={formData.correct_answer}
              onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
              required
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
            />
            <div className="grid grid-cols-2 gap-4">
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
              >
                <option value="vocabulaire">📚 Vocabulaire</option>
                <option value="grammaire">📖 Grammaire</option>
                <option value="situations">💬 Situations</option>
                <option value="expressions">🗣️ Expressions</option>
              </select>
              <select
                value={formData.level}
                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
              >
                <option value="beginner">🌱 Niveau 1</option>
                <option value="intermediate">🌿 Niveau 2</option>
                <option value="advanced">🌳 Niveau 3</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="bg-secondary text-white font-nunito font-bold py-2 px-6 rounded-lg flex items-center gap-2">
                <Save className="w-5 h-5" />
                Sauvegarder
              </button>
              <button type="button" onClick={resetForm} className="bg-stone-200 text-charcoal font-nunito font-bold py-2 px-6 rounded-lg">
                Annuler
              </button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="text-left font-nunito font-bold text-charcoal px-4 py-3">Type</th>
              <th className="text-left font-nunito font-bold text-charcoal px-4 py-3">Question</th>
              <th className="text-left font-nunito font-bold text-charcoal px-4 py-3">Image</th>
              <th className="text-left font-nunito font-bold text-charcoal px-4 py-3">Catégorie</th>
              <th className="text-left font-nunito font-bold text-charcoal px-4 py-3">Niveau</th>
              <th className="text-right font-nunito font-bold text-charcoal px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredQuestions.map((q) => (
              <tr key={q.id} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-nunito ${
                    q.question_type === 'fill_blank' 
                      ? 'bg-secondary/10 text-secondary' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {q.question_type === 'fill_blank' ? '✏️ Trous' : '📝 QCM'}
                  </span>
                </td>
                <td className="px-4 py-3 font-nunito">
                  <div>
                    <p className="font-medium">{q.question}</p>
                    {q.blank_sentence && (
                      <p className="text-sm text-charcoal/60 mt-1">"{q.blank_sentence}"</p>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {q.image_url ? (
                      <img 
                        src={`${BACKEND_URL}${q.image_url}`}
                        alt="Quiz"
                        className="w-12 h-12 object-cover rounded-lg border border-stone-200"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center text-stone-400">
                        <Image className="w-5 h-5" />
                      </div>
                    )}
                    <label className="cursor-pointer p-1.5 hover:bg-stone-100 rounded-lg">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, q.id)}
                        disabled={uploadingImage === q.id}
                      />
                      {uploadingImage === q.id ? (
                        <span className="text-xs">⏳</span>
                      ) : (
                        <Plus className="w-4 h-4 text-primary" />
                      )}
                    </label>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="bg-accent/10 text-accent px-2 py-1 rounded text-sm">{q.category}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="bg-primary/10 text-primary px-2 py-1 rounded text-sm">
                    {q.level === 'beginner' ? '🌱 N1' : q.level === 'intermediate' ? '🌿 N2' : '🌳 N3'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => handleEdit(q)} className="p-2 text-secondary hover:bg-secondary/10 rounded-lg">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(q.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredQuestions.length === 0 && (
          <div className="text-center py-12 text-charcoal/50 font-nunito">
            {activeQuizTab === 'grand_quiz' 
              ? '🏆 Aucune question de Grand Quiz. Ajoutez des questions personnalisées ou elles seront générées automatiquement.'
              : 'Aucune question de quiz pour les catégories.'
            }
          </div>
        )}
      </div>
    </div>
  );
};

// ============ CULTURE MANAGER ============
const CultureManager = ({ token, levels }) => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [levelFilter, setLevelFilter] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    emoji: '☕',
    description_fr: '',
    description_en: '',
    level: 'beginner',
    category: 'traditions'
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/culture`);
      setItems(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/culture/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Élément modifié!');
      } else {
        await axios.post(`${API}/admin/culture`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Élément ajouté!');
      }
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet élément?')) return;
    try {
      await axios.delete(`${API}/admin/culture/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Élément supprimé!');
      fetchItems();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      title: item.title,
      emoji: item.emoji,
      description_fr: item.description_fr,
      description_en: item.description_en,
      level: item.level,
      category: item.category
    });
    setShowForm(true);
  };

  const handleImageUpload = async (e, itemId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(itemId);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('item_type', 'culture');
    formDataUpload.append('item_id', itemId);
    
    try {
      await axios.post(`${API}/admin/upload/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Image uploadée!');
      fetchItems();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingImage(null);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      emoji: '☕',
      description_fr: '',
      description_en: '',
      level: 'beginner',
      category: 'traditions'
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getLevelLabel = (level) => {
    const labels = { beginner: '🌱 N1', intermediate: '🌿 N2', advanced: '🌳 N3' };
    return labels[level] || level;
  };

  const getCategoryLabel = (category) => {
    const labels = { 
      traditions: '🎭 Traditions', 
      etiquette: '🤝 Étiquette', 
      daily_life: '☀️ Vie quotidienne',
      gastronomy: '🍽️ Gastronomie'
    };
    return labels[category] || category;
  };

  const filteredItems = levelFilter 
    ? items.filter(item => item.level === levelFilter)
    : items;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h2 className="font-playfair text-3xl font-bold text-charcoal">
            🇲🇦 Culture A-lMaghrib
          </h2>
          {levelFilter && (
            <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-nunito font-semibold flex items-center gap-2">
              Filtré: {getLevelLabel(levelFilter)}
              <button onClick={() => setLevelFilter(null)} className="hover:text-amber-900">✕</button>
            </span>
          )}
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="add-culture-btn"
          className="bg-amber-500 hover:bg-amber-600 text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Ajouter un élément
        </button>
      </div>

      {/* Level Filter Buttons */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setLevelFilter(null)}
          className={`px-4 py-2 rounded-lg font-nunito font-semibold transition-colors ${
            !levelFilter ? 'bg-amber-500 text-white' : 'bg-stone-100 text-charcoal hover:bg-stone-200'
          }`}
        >
          Tous ({items.length})
        </button>
        {['beginner', 'intermediate', 'advanced'].map((level) => {
          const count = items.filter(i => i.level === level).length;
          return (
            <button
              key={level}
              onClick={() => setLevelFilter(level)}
              className={`px-4 py-2 rounded-lg font-nunito font-semibold transition-colors ${
                levelFilter === level ? 'bg-amber-500 text-white' : 'bg-stone-100 text-charcoal hover:bg-stone-200'
              }`}
            >
              {getLevelLabel(level)} ({count})
            </button>
          );
        })}
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-stone-200"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-playfair text-xl font-bold text-charcoal">
              {editingId ? 'Modifier' : 'Ajouter'} un élément culturel
            </h3>
            <button onClick={resetForm} className="text-charcoal/50 hover:text-charcoal">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Titre (ex: The Tea Ceremony)"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
              />
              <EmojiPicker
                value={formData.emoji}
                onChange={(emoji) => setFormData({ ...formData, emoji: emoji })}
                placeholder="Emoji"
              />
            </div>
            <textarea
              placeholder="Description en français..."
              value={formData.description_fr}
              onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
              required
              rows={3}
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
            />
            <textarea
              placeholder="Description in English..."
              value={formData.description_en}
              onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
              required
              rows={3}
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
            />
            <div className="grid grid-cols-2 gap-4">
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
              >
                <option value="traditions">🎭 Traditions</option>
                <option value="etiquette">🤝 Étiquette</option>
                <option value="daily_life">☀️ Vie quotidienne</option>
                <option value="gastronomy">🍽️ Gastronomie</option>
              </select>
              <select
                value={formData.level}
                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
              >
                <option value="beginner">🌱 Niveau 1</option>
                <option value="intermediate">🌿 Niveau 2</option>
                <option value="advanced">🌳 Niveau 3</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white font-nunito font-bold py-2 px-6 rounded-lg flex items-center gap-2">
                <Save className="w-5 h-5" />
                Sauvegarder
              </button>
              <button type="button" onClick={resetForm} className="bg-stone-200 hover:bg-stone-300 text-charcoal font-nunito font-bold py-2 px-6 rounded-lg">
                Annuler
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Culture Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredItems.length === 0 ? (
          <div className="col-span-2 bg-white rounded-xl shadow-sm border border-stone-200 text-center py-12 text-charcoal/50 font-nunito">
            🇲🇦 Aucun élément culturel. Cliquez sur "Ajouter un élément" pour commencer.
          </div>
        ) : (
          filteredItems.map((item) => (
            <div key={item.id} className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
              {/* Image Section */}
              <div className="h-40 bg-gradient-to-br from-amber-100 to-orange-100 relative">
                {item.image_url ? (
                  <img 
                    src={`${BACKEND_URL}${item.image_url}`}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-6xl">
                    {item.emoji}
                  </div>
                )}
                {/* Upload button */}
                <label className="absolute bottom-2 right-2 bg-white/90 hover:bg-white p-2 rounded-lg cursor-pointer shadow-sm">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e, item.id)}
                    disabled={uploadingImage === item.id}
                  />
                  {uploadingImage === item.id ? (
                    <span className="text-sm">⏳</span>
                  ) : (
                    <Image className="w-4 h-4 text-charcoal" />
                  )}
                </label>
              </div>
              
              {/* Content */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{item.emoji}</span>
                    <h4 className="font-nunito font-bold text-charcoal">{item.title}</h4>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleEdit(item)} className="p-2 text-secondary hover:bg-secondary/10 rounded-lg">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="font-nunito text-sm text-charcoal/70 line-clamp-2 mb-3">
                  {item.description_fr}
                </p>
                <div className="flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded text-xs font-nunito">
                    {getCategoryLabel(item.category)}
                  </span>
                  <span className="bg-primary/10 text-primary px-2 py-1 rounded text-xs font-nunito">
                    {getLevelLabel(item.level)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ============ GRAMMAR MINI-QUIZ MANAGER ============
const GrammarMiniQuizManager = ({ token, levels }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [grammarLessons, setGrammarLessons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedGrammar, setSelectedGrammar] = useState(null);
  const [formData, setFormData] = useState({
    grammar_id: '',
    question: '',
    options: ['', '', '', ''],
    correct_answer: '',
    question_type: 'mcq',
    blank_sentence: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [quizzesRes, grammarRes] = await Promise.all([
        axios.get(`${API}/grammar-mini-quiz`),
        axios.get(`${API}/grammar`)
      ]);
      setQuizzes(quizzesRes.data);
      setGrammarLessons(grammarRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/grammar-mini-quiz/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Question modifiée!');
      } else {
        await axios.post(`${API}/admin/grammar-mini-quiz`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Question ajoutée!');
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette question?')) return;
    try {
      await axios.delete(`${API}/admin/grammar-mini-quiz/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Question supprimée!');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (quiz) => {
    setEditingId(quiz.id);
    setFormData({
      grammar_id: quiz.grammar_id,
      question: quiz.question,
      options: quiz.options || ['', '', '', ''],
      correct_answer: quiz.correct_answer,
      question_type: quiz.question_type || 'mcq',
      blank_sentence: quiz.blank_sentence || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      grammar_id: selectedGrammar || '',
      question: '',
      options: ['', '', '', ''],
      correct_answer: '',
      question_type: 'mcq',
      blank_sentence: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const getGrammarTitle = (grammarId) => {
    const grammar = grammarLessons.find(g => g.id === grammarId);
    return grammar ? `${grammar.icon} ${grammar.title}` : grammarId;
  };

  const getLevelLabel = (level) => {
    const labels = { beginner: '🌱 N1', intermediate: '🌿 N2', advanced: '🌳 N3' };
    return labels[level] || level;
  };

  // Group quizzes by grammar lesson
  const quizzesByGrammar = grammarLessons.map(grammar => ({
    ...grammar,
    quizzes: quizzes.filter(q => q.grammar_id === grammar.id)
  })).filter(g => selectedGrammar ? g.id === selectedGrammar : true);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h2 className="font-playfair text-3xl font-bold text-charcoal">
            📝 Mini-Quiz Grammaire
          </h2>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (!showForm) setFormData(prev => ({ ...prev, grammar_id: selectedGrammar || '' }));
          }}
          data-testid="add-mini-quiz-btn"
          className="bg-secondary hover:bg-secondary/90 text-white font-nunito font-bold py-2 px-4 rounded-lg flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Ajouter une question
        </button>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="font-nunito text-blue-800">
          <strong>📝 Mini-Quiz :</strong> Créez des questions liées directement à chaque leçon de grammaire. 
          Ces questions apparaissent à la fin de chaque leçon pour permettre à l'élève de pratiquer immédiatement.
        </p>
      </div>

      {/* Grammar Filter */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={() => setSelectedGrammar(null)}
          className={`px-4 py-2 rounded-lg font-nunito font-semibold transition-colors ${
            !selectedGrammar ? 'bg-secondary text-white' : 'bg-stone-100 text-charcoal hover:bg-stone-200'
          }`}
        >
          Toutes les leçons ({grammarLessons.length})
        </button>
        {grammarLessons.slice(0, 5).map((grammar) => {
          const count = quizzes.filter(q => q.grammar_id === grammar.id).length;
          return (
            <button
              key={grammar.id}
              onClick={() => setSelectedGrammar(grammar.id)}
              className={`px-4 py-2 rounded-lg font-nunito font-semibold transition-colors ${
                selectedGrammar === grammar.id ? 'bg-secondary text-white' : 'bg-stone-100 text-charcoal hover:bg-stone-200'
              }`}
            >
              {grammar.icon} {grammar.title.substring(0, 15)}... ({count})
            </button>
          );
        })}
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-stone-200"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-playfair text-xl font-bold text-charcoal">
              {editingId ? 'Modifier' : 'Ajouter'} une question
            </h3>
            <button onClick={resetForm} className="text-charcoal/50 hover:text-charcoal">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Grammar Lesson Selector */}
            <div>
              <label className="block font-nunito font-semibold text-charcoal mb-2">
                Leçon de grammaire liée:
              </label>
              <select
                value={formData.grammar_id}
                onChange={(e) => setFormData({ ...formData, grammar_id: e.target.value })}
                required
                className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
              >
                <option value="">-- Sélectionnez une leçon --</option>
                {grammarLessons.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.icon} {g.title} ({getLevelLabel(g.level)})
                  </option>
                ))}
              </select>
            </div>

            {/* Question Type */}
            <div>
              <label className="block font-nunito font-semibold text-charcoal mb-2">Type de question:</label>
              <div className="flex gap-3">
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border-2 transition-colors ${
                  formData.question_type === 'mcq' ? 'border-secondary bg-secondary/10' : 'border-stone-200 hover:border-stone-300'
                }`}>
                  <input
                    type="radio"
                    name="mini_question_type"
                    value="mcq"
                    checked={formData.question_type === 'mcq'}
                    onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
                    className="hidden"
                  />
                  <span className="font-nunito">📝 QCM</span>
                </label>
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border-2 transition-colors ${
                  formData.question_type === 'fill_blank' ? 'border-secondary bg-secondary/10' : 'border-stone-200 hover:border-stone-300'
                }`}>
                  <input
                    type="radio"
                    name="mini_question_type"
                    value="fill_blank"
                    checked={formData.question_type === 'fill_blank'}
                    onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
                    className="hidden"
                  />
                  <span className="font-nunito">✏️ Phrase à trous</span>
                </label>
              </div>
            </div>

            <input
              type="text"
              placeholder="Question"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              required
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
            />

            {formData.question_type === 'fill_blank' && (
              <div>
                <label className="block font-nunito font-semibold text-charcoal mb-1">
                  Phrase à compléter (utilisez ___ pour le trou):
                </label>
                <input
                  type="text"
                  placeholder="Ex: Ana ___ Marrakech"
                  value={formData.blank_sentence}
                  onChange={(e) => setFormData({ ...formData, blank_sentence: e.target.value })}
                  className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="font-nunito font-semibold text-charcoal">Options (4 choix):</label>
              {formData.options.map((opt, i) => (
                <input
                  key={i}
                  type="text"
                  placeholder={`Option ${i + 1}`}
                  value={opt}
                  onChange={(e) => updateOption(i, e.target.value)}
                  required
                  className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-2 font-nunito"
                />
              ))}
            </div>

            <input
              type="text"
              placeholder="Réponse correcte"
              value={formData.correct_answer}
              onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
              required
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito"
            />

            <div className="flex gap-3 pt-2">
              <button type="submit" className="bg-secondary hover:bg-secondary/90 text-white font-nunito font-bold py-2 px-6 rounded-lg flex items-center gap-2">
                <Save className="w-5 h-5" />
                Sauvegarder
              </button>
              <button type="button" onClick={resetForm} className="bg-stone-200 hover:bg-stone-300 text-charcoal font-nunito font-bold py-2 px-6 rounded-lg">
                Annuler
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Quizzes grouped by grammar lesson */}
      <div className="space-y-6">
        {quizzesByGrammar.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-stone-200 text-center py-12 text-charcoal/50 font-nunito">
            📖 Aucune leçon de grammaire disponible.
          </div>
        ) : (
          quizzesByGrammar.map((grammar) => (
            <div key={grammar.id} className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
              {/* Grammar Header */}
              <div className="bg-gradient-to-r from-secondary/10 to-secondary/5 border-b border-stone-200 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{grammar.icon}</span>
                  <div>
                    <h4 className="font-nunito font-bold text-charcoal">{grammar.title}</h4>
                    <span className="bg-secondary/10 text-secondary px-2 py-0.5 rounded text-xs font-nunito">
                      {getLevelLabel(grammar.level)} • {grammar.quizzes.length} question(s)
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedGrammar(grammar.id);
                    setFormData(prev => ({ ...prev, grammar_id: grammar.id }));
                    setShowForm(true);
                  }}
                  className="bg-secondary/10 hover:bg-secondary/20 text-secondary px-3 py-1 rounded-lg text-sm font-nunito font-semibold flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter
                </button>
              </div>

              {/* Questions */}
              <div className="p-4">
                {grammar.quizzes.length === 0 ? (
                  <p className="text-charcoal/40 font-nunito text-sm italic text-center py-4">
                    Aucune question liée à cette leçon. Cliquez sur "Ajouter" pour créer un mini-quiz.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {grammar.quizzes.map((quiz, idx) => (
                      <div key={quiz.id} className="flex items-start gap-3 p-3 bg-sand/50 rounded-lg border border-stone-200">
                        <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center text-secondary font-bold text-sm flex-shrink-0">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-nunito font-semibold text-charcoal">
                            {quiz.question}
                          </p>
                          {quiz.blank_sentence && (
                            <p className="font-nunito text-sm text-charcoal/60 mt-1">
                              "{quiz.blank_sentence}"
                            </p>
                          )}
                          <p className="font-nunito text-xs text-green-600 mt-1">
                            ✓ {quiz.correct_answer}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className={`px-2 py-1 rounded text-xs font-nunito ${
                            quiz.question_type === 'fill_blank' 
                              ? 'bg-secondary/10 text-secondary' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {quiz.question_type === 'fill_blank' ? '✏️' : '📝'}
                          </span>
                          <button onClick={() => handleEdit(quiz)} className="p-2 text-secondary hover:bg-secondary/10 rounded-lg">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(quiz.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ============ MODULE MANAGER ============
const ModuleManager = ({ token, levels, modules, onUpdate }) => {
  const navigate = useNavigate();
  const [contentCounts, setContentCounts] = useState({});

  useEffect(() => {
    fetchContentCounts();
  }, []);

  const fetchContentCounts = async () => {
    try {
      const [vocabRes, grammarRes, situationsRes, quizRes] = await Promise.all([
        axios.get(`${API}/vocabulary`),
        axios.get(`${API}/grammar`),
        axios.get(`${API}/situations`),
        axios.get(`${API}/quiz`)
      ]);

      const counts = {};
      ['beginner', 'intermediate', 'advanced'].forEach((level, idx) => {
        const levelNum = idx + 1;
        counts[levelNum] = {
          vocabulary: vocabRes.data.filter(v => v.level === level).length,
          grammar: grammarRes.data.filter(g => g.level === level).length,
          situations: situationsRes.data.filter(s => s.level === level).length,
          quiz: quizRes.data.filter(q => q.level === level).length
        };
      });
      setContentCounts(counts);
    } catch (error) {
      console.error('Failed to fetch content counts:', error);
    }
  };

  const getLevelKey = (levelNum) => {
    return levelNum === 1 ? 'beginner' : levelNum === 2 ? 'intermediate' : 'advanced';
  };

  const handleNavigate = (tab, levelNum) => {
    // Navigate to the appropriate tab with level filter
    const levelKey = getLevelKey(levelNum);
    // Set filter in localStorage so the tab can read it
    localStorage.setItem('admin_filter_level', levelKey);
    // Change tab - we'll use a custom event
    window.dispatchEvent(new CustomEvent('adminNavigate', { detail: { tab, level: levelKey } }));
  };

  return (
    <div>
      <h2 className="font-playfair text-3xl font-bold text-charcoal mb-6">Gestion des Modules</h2>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="font-nunito text-blue-800">
          <strong>💡 Info :</strong> Cliquez sur une catégorie pour voir et gérer le contenu filtré par niveau.
        </p>
      </div>

      <div className="space-y-6">
        {levels.map((level) => {
          const counts = contentCounts[level.level_number] || { vocabulary: 0, grammar: 0, situations: 0, quiz: 0 };
          
          return (
            <div key={level.id} className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">
                  {level.level_number === 1 ? '🌱' : level.level_number === 2 ? '🌿' : '🌳'}
                </span>
                <div>
                  <h3 className="font-playfair text-xl font-bold text-charcoal">{level.title_fr}</h3>
                  <p className="font-nunito text-sm text-charcoal/60">{level.description_fr}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-4 mt-4">
                <button
                  onClick={() => handleNavigate('vocabulary', level.level_number)}
                  className="bg-stone-50 hover:bg-primary/10 rounded-lg p-3 text-center transition-colors cursor-pointer border-2 border-transparent hover:border-primary"
                  data-testid={`module-vocab-level-${level.level_number}`}
                >
                  <p className="font-nunito text-2xl font-bold text-primary">
                    {counts.vocabulary}
                  </p>
                  <p className="font-nunito text-xs text-charcoal/60 hover:text-primary">📚 Vocabulaire</p>
                </button>
                <button
                  onClick={() => handleNavigate('grammar', level.level_number)}
                  className="bg-stone-50 hover:bg-secondary/10 rounded-lg p-3 text-center transition-colors cursor-pointer border-2 border-transparent hover:border-secondary"
                  data-testid={`module-grammar-level-${level.level_number}`}
                >
                  <p className="font-nunito text-2xl font-bold text-secondary">{counts.grammar}</p>
                  <p className="font-nunito text-xs text-charcoal/60 hover:text-secondary">📖 Grammaire</p>
                </button>
                <button
                  onClick={() => handleNavigate('situations', level.level_number)}
                  className="bg-stone-50 hover:bg-accent/10 rounded-lg p-3 text-center transition-colors cursor-pointer border-2 border-transparent hover:border-accent"
                  data-testid={`module-situations-level-${level.level_number}`}
                >
                  <p className="font-nunito text-2xl font-bold text-accent">{counts.situations}</p>
                  <p className="font-nunito text-xs text-charcoal/60 hover:text-accent">💬 Situations</p>
                </button>
                <button
                  onClick={() => handleNavigate('quiz', level.level_number)}
                  className="bg-stone-50 hover:bg-oasis/10 rounded-lg p-3 text-center transition-colors cursor-pointer border-2 border-transparent hover:border-oasis"
                  data-testid={`module-quiz-level-${level.level_number}`}
                >
                  <p className="font-nunito text-2xl font-bold text-oasis">{counts.quiz}</p>
                  <p className="font-nunito text-xs text-charcoal/60 hover:text-oasis">🧠 Quiz</p>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Admin;
